import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import Constants from 'src/core/utils/Constants';
import type { ApiTimetableClock, ApiTimetableItem } from 'src/core/models/api/shared/ApiTimetable';

type TimetableTimes = Pick<ApiTimetableItem, 'startdate' | 'enddate' | 'start' | 'end'>;

function formatClock(clock: ApiTimetableClock | undefined): string {
	if (!clock || typeof clock.HH !== 'string' || typeof clock.mm !== 'string') return '';
	return `${clock.HH}:${clock.mm}`;
}

/**
 * The unix timestamp is the only time the API is guaranteed to send; the pre-split { HH, mm } clock only
 * exists on sessions saved by the legacy CMS, so it is a fallback and never a requirement.
 */
export function formatTimetableClock(timestamp: number | null | undefined, clock?: ApiTimetableClock): string {
	if (typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0) {
		return dayjs.unix(timestamp).format(Constants.timeFormat);
	}
	return formatClock(clock);
}

/**
 * The moment a session sits at on the timetable axis, in unix seconds.
 */
export function resolveTimetableSeconds(
	timestamp: number | null | undefined,
	clock: ApiTimetableClock | undefined,
	dayStart: number,
): number | null {
	if (typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp > 0) {
		return timestamp;
	}

	if (!clock || typeof clock.HH !== 'string' || typeof clock.mm !== 'string') {
		return null;
	}

	const hours = Number(clock.HH);
	const minutes = Number(clock.mm);
	if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(dayStart) || dayStart <= 0) {
		return null;
	}

	return dayjs.unix(dayStart).startOf('day').unix() + hours * 3600 + minutes * 60;
}

/**
 * A page date as the beheer panel stores it (YYYY-MM-DD) in the format the app shows dates in. Anything
 * dayjs cannot read is handed back untouched, because a date the participant can read beats "Invalid Date".
 */
export function formatPageDate(date: string): string {
	const parsed = dayjs(date);

	return parsed.isValid() ? parsed.format(Constants.dateFormat) : date;
}

/**
 * The same date with its month in words ("23 juni 2026"), for a heading that names the day it covers.
 */
export function formatPageDay(date: string): string {
	const parsed = dayjs(date);

	// Its own locale, not the app-wide one: whether this module is reached before or after i18n set that
	// decided whether a month came out Dutch or English.
	return parsed.isValid() ? parsed.locale('nl').format(Constants.dayFormat) : date;
}

export function formatTimetableRange(item: TimetableTimes): string {
	const start = formatTimetableClock(item.startdate, item.start);
	const end = formatTimetableClock(item.enddate, item.end);

	if (start && end) return `${start} - ${end}`;
	return start || end;
}

export default formatTimetableRange;
